"use client";

import { usePathname } from "next/navigation";
import { storyblokEditable, type SbBlokData } from "@storyblok/react/rsc";

import { resolveBg } from "@/lib/bg-color";
import { regionFromPathname } from "@/lib/region";
import { resolveLink, type StoryblokLink } from "@/lib/storyblok-link";

type HeroButton = SbBlokData & {
  label?: string;
  link?: StoryblokLink;
};

type HeroWithMultipleButtonsBlok = SbBlokData & {
  title?: string;
  description?: string;
  buttons?: HeroButton[];
  bg_color?: string;
};

export default function HeroWithMultipleButtons({
  blok,
}: {
  blok: HeroWithMultipleButtonsBlok;
}) {
  const pathname = usePathname();
  const region = regionFromPathname(pathname);
  const { bg, onDark } = resolveBg(blok.bg_color, "purple-00");
  const buttons = (blok.buttons ?? []).filter((b) => b.label);

  const buttonClass = onDark
    ? "border-white/60 text-white hover:bg-white hover:text-purple-05"
    : "border-purple-03 text-purple-05 hover:bg-purple-03 hover:text-white";

  return (
    <section {...storyblokEditable(blok)} className={bg}>
      <div className="mx-auto flex w-full max-w-[1280px] flex-col items-start gap-10 px-[clamp(1.25rem,4vw,2.5rem)] py-10 lg:gap-14 lg:py-[61px]">
        <div className="flex flex-col gap-[23px]">
          {blok.title && (
            <h1
              className={`text-h3 md:text-[64px] font-bold leading-[1.054] tracking-[0.02em] ${
                onDark ? "text-white" : "text-purple-05"
              }`}
            >
              {blok.title}
            </h1>
          )}
          {blok.description && (
            <p
              className={`text-body md:text-h6 leading-[1.56] lg:max-w-[559px] ${
                onDark ? "text-white/85" : "text-[#595959]"
              }`}
            >
              {blok.description}
            </p>
          )}
        </div>

        {buttons.length > 0 && (
          <div className="flex flex-wrap gap-3 lg:gap-4">
            {buttons.map((button) => {
              const { href, target } = resolveLink(button.link, region);
              return (
                <a
                  key={button._uid as string}
                  {...storyblokEditable(button)}
                  href={href}
                  {...(target ? { target } : {})}
                  className={`inline-flex h-14 items-center justify-center rounded-[8px] border px-7 text-body font-bold transition-colors ${buttonClass}`}
                >
                  {button.label}
                </a>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
